import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";

export default function BrandsManager() {
  const [brands, setBrands] = useState([]);
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/brands");
    setBrands(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      await api.post("/admin/brands", { name: name.trim(), logo_url: logoUrl.trim(), order: brands.length, active: true });
      setName(""); setLogoUrl("");
      await load();
      toast.success("Brand added");
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const save = async (b) => {
    try {
      await api.put(`/admin/brands/${b.id}`, { name: b.name, logo_url: b.logo_url || "", order: b.order ?? 999, active: b.active !== false });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    try { await api.delete(`/admin/brands/${b.id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= brands.length) return;
    const ids = brands.map(b => b.id);
    [ids[idx], ids[t]] = [ids[t], ids[idx]];
    try { await api.put("/admin/brands/reorder", { ordered_ids: ids }); await load(); }
    catch { toast.error("Reorder failed"); }
  };

  const patchLocal = (id, patch) => setBrands(bs => bs.map(b => b.id === id ? { ...b, ...patch } : b));

  return (
    <div className="space-y-6" data-testid="brands-manager">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-display text-base font-semibold text-[#0B132B] mb-3">Add Brand</h3>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <Input placeholder="Brand name" value={name} onChange={e => setName(e.target.value)} data-testid="brand-name-input" />
          <Input placeholder="Logo URL (optional)" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} data-testid="brand-logo-input" />
          <Button onClick={add} disabled={saving} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="brand-add-btn">
            <Plus className="w-4 h-4 me-1" /> Add Brand
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl divide-y">
        {brands.length === 0 && <p className="p-8 text-center text-slate-500">No brands yet.</p>}
        {brands.map((b, idx) => (
          <div key={b.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`brand-row-${b.id}`}>
            <div className="flex flex-col gap-1">
              <Button size="icon" variant="outline" className="rounded-full w-8 h-8" disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="Up"><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="outline" className="rounded-full w-8 h-8" disabled={idx === brands.length - 1} onClick={() => move(idx, 1)} aria-label="Down"><ArrowDown className="w-3.5 h-3.5" /></Button>
            </div>
            {b.logo_url ? <img src={b.logo_url} alt={b.name} className="w-10 h-10 object-contain bg-slate-50 rounded-lg" /> : <div className="w-10 h-10 bg-slate-100 rounded-lg" />}
            <Input value={b.name} onChange={e => patchLocal(b.id, { name: e.target.value })} className="flex-1 min-w-[140px]" data-testid={`brand-name-${b.id}`} />
            <Input placeholder="Logo URL" value={b.logo_url || ""} onChange={e => patchLocal(b.id, { logo_url: e.target.value })} className="flex-1 min-w-[180px]" />
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Active</Label>
              <Switch checked={b.active !== false} onCheckedChange={v => patchLocal(b.id, { active: v })} data-testid={`brand-active-${b.id}`} />
            </div>
            <Button size="sm" onClick={() => save(b)} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid={`brand-save-${b.id}`}><Save className="w-3.5 h-3.5 me-1" /> Save</Button>
            <Button size="sm" variant="outline" onClick={() => del(b)} className="rounded-full text-rose-600 border-rose-200" data-testid={`brand-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
